import { type TcsDocumentRow } from '@utazzo/shared';
import { UzButton, UzConfirmDialog } from '@utazzo/ui';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

import { MAX_TCS_DOC_BYTES } from '../tcs-challans.api';
import {
  useDeleteCertificate,
  useInvoiceCertificates,
  useUploadCertificate,
} from '../use-tcs-invoices';

import { formatLocalDMY } from '@/lib/date-format';

interface TcsInvoiceCertificatesProps {
  invoiceId: string;
}

/**
 * The TCS certificate(s) for a single invoice: upload any number of files
 * (presign -> PUT -> create), download, delete. Independent of any challan.
 */
export const TcsInvoiceCertificates = ({
  invoiceId,
}: TcsInvoiceCertificatesProps): React.JSX.Element => {
  const { data: documents = [], isLoading } = useInvoiceCertificates(invoiceId);
  const upload = useUploadCertificate();
  const remove = useDeleteCertificate();
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TcsDocumentRow | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (file.size > MAX_TCS_DOC_BYTES) {
          toast.error(`${file.name} is larger than 25 MB`);
          continue;
        }
        try {
          await upload.mutateAsync({ invoiceId, file });
          toast.success(`Uploaded ${file.name}`);
        } catch (err) {
          toast.error(err instanceof Error ? err.message : `Failed to upload ${file.name}`);
        }
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-zinc-600">TCS certificate(s) for this invoice.</p>
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          aria-label="Choose certificate files"
          data-testid="tcs-cert-input"
          onChange={(e) => void handleFiles(e.target.files)}
        />
        <UzButton
          size="sm"
          leftIcon={<Upload className="h-4 w-4" />}
          loading={uploading}
          onClick={() => fileRef.current?.click()}
        >
          Upload certificate
        </UzButton>
      </div>

      {isLoading ? (
        <p className="text-sm text-zinc-500">Loading...</p>
      ) : documents.length === 0 ? (
        <p className="text-sm text-zinc-500">No certificate uploaded yet.</p>
      ) : (
        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
          {documents.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="flex min-w-0 items-center gap-3">
                <FileText className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-zinc-900">{d.fileName}</p>
                  <p className="text-xs text-zinc-500">{formatLocalDMY(d.createdAt)}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <a
                  href={d.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Download ${d.fileName}`}
                  className="rounded p-2 text-zinc-500 hover:bg-zinc-100"
                >
                  <Download className="h-4 w-4" />
                </a>
                <UzButton
                  size="sm"
                  variant="ghost"
                  aria-label={`Delete ${d.fileName}`}
                  onClick={() => setDeleteTarget(d)}
                >
                  <Trash2 className="h-4 w-4 text-zinc-400" />
                </UzButton>
              </div>
            </li>
          ))}
        </ul>
      )}

      <UzConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this certificate?"
        description={deleteTarget ? `"${deleteTarget.fileName}" will be removed.` : ''}
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={async () => {
          if (deleteTarget) {
            await remove.mutateAsync({ invoiceId, docId: deleteTarget.id });
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
};
